import CustomTable, {
  TABLE_SCROLL_HEIGHT,
} from "@/shared/components/ui/Button/CustomTable";
import PaginationInfo from "@/shared/components/ui/Pagination/PaginationInfo";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import { useCallback, useMemo, useState } from "react";
import { useReviewTrialRequest, useFetchUsersList } from "@/shared/hooks";
import {
  ACCESS_REQUEST_COLUMNS,
  createAccessRequestRenderCell,
} from "@/shared/utils/commonConfigs";

const PendingMembers = () => {
  const { data: allUserList } = useFetchUsersList();
  const { mutate: reviewTrialRequest, isPending: isPendingReviewTrialRequest } =
    useReviewTrialRequest();

  const [filterValue, setFilterValue] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const handleReviewTrialRequest = useCallback(
    (userId, action) => reviewTrialRequest({ userId, action }),
    [reviewTrialRequest],
  );

  const accessRequestRenderCell = useMemo(
    () =>
      createAccessRequestRenderCell(
        handleReviewTrialRequest,
        isPendingReviewTrialRequest,
      ),
    [handleReviewTrialRequest, isPendingReviewTrialRequest],
  );

  const pendingUsers = useMemo(
    () =>
      (allUserList ?? []).filter(
        (u) => u.trial_approved_at === null && u.plan !== "reject",
      ),
    [allUserList],
  );

  const filteredData = useMemo(() => {
    if (!filterValue) return pendingUsers;
    const q = filterValue.toLowerCase();
    return pendingUsers.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q),
    );
  }, [pendingUsers, filterValue]);

  const onSearchChange = useCallback((value) => {
    setFilterValue(value);
    setPage(1);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-[1.375rem] text-gray-800 font-bold">
        Pending Join Requests
      </h1>
      <div className="rounded-xl p-4 bg-white shadow w-full h-[calc(100dvh-90px)]">
        <div className="flex flex-col gap-4 w-full h-full">
          <div className="flex justify-between items-center gap-4">
            <div className="flex gap-2 items-center w-full">
              <SearchBar
                value={filterValue}
                setFilterValue={setFilterValue}
                onChange={onSearchChange}
                placeholder="Search by name or email..."
                className="max-w-[320px]"
              />
              <PaginationInfo
                totalCount={filteredData.length}
                currentPage={page}
                rowsPerPage={rowsPerPage}
                itemName="request"
              />
            </div>
          </div>

          {filteredData.length > 0 ? (
            <CustomTable
              renderCell={accessRequestRenderCell}
              columns={ACCESS_REQUEST_COLUMNS}
              data={filteredData}
              totalCount={filteredData.length}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              page={page}
              onPageChange={setPage}
              height={TABLE_SCROLL_HEIGHT}
            />
          ) : (
            <p className="text-gray-500 text-center h-full flex items-center justify-center">
              No pending join requests.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingMembers;
